/**
 * LoginPage — реальная страница входа.
 *
 * Режимы:
 * - Dev-bypass (если VITE_DEV_AUTH_BYPASS_TOKEN задан) — кнопка «Войти как Janni Kremm».
 * - Обычный — email + password (пока stub, Janni будет логиниться через Google OAuth).
 */
import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Sparkles, LogIn } from 'lucide-react';

import { api } from '@/lib/api';
import { useAuthStore } from '@/store/authStore';
import { isDevBypassEnabled, devBypassLogin } from '@/lib/devBypass';
import type { Account } from '@/hooks/useAccount';

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const setAuth = useAuthStore((s) => s.setAuth);
  const from = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname ?? '/';

  const [email, setEmail] = useState('janni.kremm@gmail.com');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleBypass = async () => {
    setError(null);
    setLoading(true);
    const acc = await devBypassLogin();
    setLoading(false);
    if (acc) {
      navigate(from, { replace: true });
    } else {
      setError('Dev-bypass failed. Check backend logs.');
    }
  };

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const r = await api.post<{ access_token: string; refresh_token: string; token_type: string }>(
        '/auth/login',
        { email, password },
      );
      // FIX 2026-08-30: token saved to localStorage BEFORE /account/me,
      // otherwise the request interceptor (lib/api.ts) sends no Authorization
      // header and the backend returns 401.
      localStorage.setItem('cs_access_token', r.data.access_token);
      // Fetch profile
      const me = await api.get<Account>('/account/me');
      setAuth(me.data, r.data.access_token);
      navigate(from, { replace: true });
    } catch (err) {
      const msg = (err as { response?: { data?: { error?: { message?: string } } } })
        ?.response?.data?.error?.message;
      setError(msg ?? 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-surface-950 p-6">
      <div className="card max-w-md w-full">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-500 to-accent-orange flex items-center justify-center shadow-glow-primary">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="font-semibold">Content Studio</div>
            <div className="text-xs text-surface-500">AI Content Platform</div>
          </div>
        </div>

        {isDevBypassEnabled() && (
          <>
            <div className="mb-4 p-3 rounded-xl bg-primary-500/10 border border-primary-500/20 text-sm text-primary-200">
              <div className="font-medium mb-1">Dev-bypass активен</div>
              <div className="text-xs text-primary-300/70">
                Локальный деплой — вход одной кнопкой как суперадмин Janni Kremm.
              </div>
            </div>
            <button onClick={handleBypass} disabled={loading} className="btn-primary w-full flex items-center justify-center gap-2 mb-4">
              <LogIn className="w-4 h-4" />
              {loading ? 'Входим…' : 'Войти как Janni Kremm'}
            </button>
            <div className="relative my-4 text-center text-xs text-surface-500">
              <span className="bg-surface-900 px-2 relative z-10">или</span>
              <div className="absolute inset-x-0 top-1/2 h-px bg-surface-800" />
            </div>
          </>
        )}

        <form onSubmit={handleEmailLogin} className="space-y-3">
          <div>
            <label className="text-xs text-surface-400 mb-1 block">Email</label>
            <input
              type="email"
              className="input w-full"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div>
            <label className="text-xs text-surface-400 mb-1 block">Пароль</label>
            <input
              type="password"
              className="input w-full"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <div className="text-xs text-surface-500 mt-1">
              В MVP пароль не используется (Janni логинится через dev-bypass / Google OAuth).
            </div>
          </div>
          {error && (
            <div className="text-sm text-danger p-2 rounded-lg bg-danger-tint/10 border border-danger-tint/20">
              {error}
            </div>
          )}
          <button type="submit" disabled={loading} className="btn-ghost w-full">
            Войти по email
          </button>
        </form>
      </div>
    </div>
  );
}
